import type { FC, ReactNode, SVGProps } from "react"
import { useState } from "react"
import {
	IconChartActivityOutline18 as Activity,
	IconCode2Outline18 as ApiCode,
	IconAtom2Outline18 as Atom,
	IconBookOpen4Outline18 as BookOpen,
	IconBugOutline18 as Bug,
	IconCheckOutline18 as Check,
	IconChevronRightOutline18 as ChevronRight,
	IconCircleArrowsOppositeDirectionOutline18 as CircleArrows,
	IconClockRotateAnticlockwiseOutline18 as ClockRotate,
	IconConnectedDots2Outline18 as ConnectedDots,
	IconDatabaseOutline18 as Database,
	IconEnvelopeArrowRightOutline18 as EnvelopeSend,
	IconCubeOutline18 as FeatureRequests,
	IconCodeBranchOutline18 as GitBranchPlus,
	IconGlobe2Outline18 as Globe,
	IconLifeRingOutline18 as LifeRing,
	IconMessageSparkleOutline18 as MessageSparkle,
	IconTelescopeOutline18 as Telescope,
} from "nucleo-ui-outline-18"

import { defaultAiChatWelcomeMessage } from "@productlane/lib/src/ai-chat-defaults"
import { cn } from "@productlane/lib/src/cn"
import {
	COVERAGE_BANDS,
	coverageAdvice,
	coverageScore,
	coverageTierIndex,
	coverageTierLabel,
} from "@productlane/lib/src/knowledge-coverage"
import {
	Badge,
	Button,
	Dialog,
	Dot,
	Input,
	Label,
	ProgressBar,
	SlackIconGray,
	Switch,
	Textarea,
} from "@productlane/ui"
import { AiAssistantIcon } from "@productlane/ui/src/icons/project/ai-assistant-icon"
import { EnvelopeDuotoneIcon } from "@productlane/ui/src/icons/project/envelope-duotone-icon"
import { GitHubIcon } from "@productlane/ui/src/icons/project/github-icon"
import { MessagesDuotoneIcon } from "@productlane/ui/src/icons/project/messages-duotone-icon"
import { MicrosoftTeamsIcon } from "@productlane/ui/src/icons/project/microsoftteams-icon"

import { CollapsiblePromptTextarea } from "@/components/collapsible-prompt-textarea"
import { docsDefaultExampleQuestions } from "@/constants"
import { isDevEnvironment } from "@/env"
import { NotFound } from "@/screens/not-found"
import { SettingsHeader } from "@/screens/settings/settings-header"
import { settingsNavGroupClass } from "@/screens/settings/settings-pages/settings-nav-row"
import { SettingItem } from "@/screens/settings/settings-switches/setting-item"
import {
	SettingsCardInsetContext,
	SettingsContainer,
} from "@/screens/settings/settings-switches/settings-container"
import { DesignIssueHeader } from "./design-issue-header"

type IconType = FC<SVGProps<SVGSVGElement>>
type Version = "A" | "B" | "C"

const WORKSPACE_NAME = "Acme"
const REPO = "acme/app"
const SCREENS_TOTAL = 34
const SCREENS_DONE = 12
// What the codebase backfill has written so far, for the connected state.
const CONNECTED_COVERAGE = { chars: 38_400, articles: 12, thin: 0, groups: [] }

const GATE_TITLE = "Give your agent something to read"
const GATE_DESCRIPTION =
	"It answers from your code and your help center. Connect one to turn it on."

const SOFT_TITLE = "Your agent has nothing to read yet"
const SOFT_DESCRIPTION =
	"Until it can read your code or docs, it passes almost every question to your team."

const WRITING_TITLE = "Writing your help center"
const WRITING_DESCRIPTION =
	"The agent answers from each article as soon as it is written."
const WRITING_ON_DESCRIPTION =
	"The agent is on. It answers from each article as soon as it is written."

const AGENT_DESCRIPTION =
	"Activate the Productlane Agent for your widget, live chat and docs."

const AGENT_ROW_DESCRIPTION: Record<Version, string> = {
	A: "Connect GitHub or your docs to turn it on.",
	B: "Needs your code or docs before it can go live.",
	C: "Works best with GitHub or your docs connected.",
}

const AGENT_SWITCH_DESCRIPTION: Record<Version, string> = {
	A: "Connect GitHub or import your docs to turn it on.",
	B: AGENT_DESCRIPTION,
	C: "Works best with GitHub or your docs connected.",
}

const DEPLOY_DESCRIPTION =
	"Pick the channels the agent answers on. Upvotes and threads pushed from Linear stay with your team."
const DEPLOY_GATED_DESCRIPTION =
	"Connect GitHub or your docs first, then pick the channels the agent answers on."

const VERSION_LINE: Record<Version, string> = {
	A: "Hard gate. Switches stay locked, and a click opens the connect dialog.",
	B: "Setup card. One card replaces the switch, and Deploy appears once a source exists.",
	C: "Soft gate. Switches work; turning on with nothing to read asks first.",
}

// ---------------------------------------------------------------------------
// Markers and frames

function Mark({ kind }: { kind: "New" | "Changed" }) {
	return <Badge variant={kind === "New" ? "important" : "amber"}>{kind}</Badge>
}

function Titled({
	children,
	mark,
}: {
	children: ReactNode
	mark?: "New" | "Changed"
}) {
	if (!mark) return <>{children}</>
	return (
		<span className="flex items-center gap-2">
			{children}
			<Mark kind={mark} />
		</span>
	)
}

// The settings shell's main column at its real 704px, padding included.
function SettingsFrame({ children }: { children: ReactNode }) {
	return (
		<div className="overflow-hidden rounded-xl bg-white ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10">
			<main className="flex w-[704px] flex-col px-8 py-16">{children}</main>
		</div>
	)
}

function ScreenRow({
	title,
	note,
	children,
}: {
	title: string
	note?: string
	children: ReactNode
}) {
	return (
		<section>
			<div className="mb-4">
				<div className="text-xs font-medium text-tertiary">{title}</div>
				{note ? (
					<p className="mt-1 max-w-[704px] text-[13px] text-secondary">
						{note}
					</p>
				) : null}
			</div>
			<div className="flex flex-nowrap items-start gap-10 overflow-x-auto pb-4">
				{children}
			</div>
		</section>
	)
}

function Column({
	version,
	line,
	children,
}: {
	version: Version
	line: string
	children: ReactNode
}) {
	return (
		<div className="w-[704px] flex-none">
			<div className="mb-3">
				<div className="text-sm font-semibold text-primary">
					Version {version}
				</div>
				<p className="mt-0.5 text-[13px] text-secondary">{line}</p>
			</div>
			{children}
		</div>
	)
}

// ---------------------------------------------------------------------------
// Page 1: Settings / AI (ai-overview-page.tsx)

// SettingsNavRow's markup, with a slot for the design marker and no navigation.
function NavRow({
	icon: Icon,
	title,
	description,
	status,
	trailing,
	mark,
}: {
	icon: IconType
	title: string
	description: string
	status?: boolean
	trailing?: ReactNode
	mark?: "New" | "Changed"
}) {
	return (
		<div className="flex items-center justify-between gap-4 px-4 py-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/50">
			<div className="flex items-center gap-3.5">
				<span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-gray-100 text-primary dark:bg-gray-800 [&_svg]:h-[18px] [&_svg]:w-[18px]">
					<Icon />
				</span>
				<div className="flex flex-col gap-0.5">
					<span className="text-sm font-medium text-primary">
						<Titled mark={mark}>{title}</Titled>
					</span>
					<span className="text-xs text-secondary">{description}</span>
				</div>
			</div>
			<div className="flex flex-none items-center gap-2">
				{trailing ??
					(status !== undefined && (
						<span
							className={cn(
								"flex items-center gap-1.5 text-sm",
								status ? "text-secondary" : "text-tertiary opacity-70",
							)}
						>
							<Dot
								color={status ? "green" : "gray"}
								size="sm"
								className={cn(!status && "opacity-50")}
							/>
							{status ? "Enabled" : "Disabled"}
						</span>
					))}
				<ChevronRight className="h-3.5 w-3.5 flex-none text-tertiary" />
			</div>
		</div>
	)
}

function AiOverviewReplica({ version }: { version: Version }) {
	return (
		<>
			<SettingsHeader
				title="AI"
				subtitle="Configure Productlane's AI across your workspace."
				docsArticle="c8cfc4d9-dec0-4188-b31c-f55ffd758d58"
			/>

			<div className={`${settingsNavGroupClass} mb-8`}>
				<NavRow
					icon={Activity}
					title="AI usage and credits"
					description="0/50 resolutions · $0.00/$5.00 credits"
				/>
			</div>

			<div className={settingsNavGroupClass}>
				<NavRow
					icon={Atom}
					title="Agent"
					description={AGENT_ROW_DESCRIPTION[version]}
					status={false}
					trailing={
						version === "B" ? (
							<Badge variant="important">Set up</Badge>
						) : undefined
					}
					mark="Changed"
				/>
				<NavRow
					icon={AiAssistantIcon}
					title="AI Assistant"
					description="Ask the in-thread assistant to research and answer for you."
					status
				/>
				<NavRow
					icon={CircleArrows}
					title="Close loop"
					description="Tell customers when the Linear issue or project they asked for ships."
					status={false}
				/>
				<NavRow
					icon={ClockRotate}
					title="Routines"
					description="Tag, prioritize, and route every thread as its messages arrive."
					status={false}
				/>
				<NavRow
					icon={MessageSparkle}
					title="AI Drafts"
					description="Populate the composer with a draft reply in the background."
					status
				/>
				<NavRow
					icon={BookOpen}
					title="Self-updating docs"
					description="Draft help center articles from shipped issues and commits."
					status={false}
				/>
				<NavRow
					icon={Telescope}
					title="Release Intelligence"
					description="Generate changelog drafts from Linear issues and GitHub commits."
					status={false}
				/>
			</div>

			<div className="mb-3 mt-8 flex items-center gap-2">
				<span className="text-sm font-medium text-primary">Integrations</span>
			</div>

			<div className={settingsNavGroupClass}>
				<NavRow
					icon={GitHubIcon}
					title="GitHub"
					description="Ground answers and draft docs from your connected repositories."
				/>
				<NavRow
					icon={Database}
					title="PostgreSQL"
					description="Let the AI Assistant run read-only queries against your database."
				/>
				<NavRow
					icon={ConnectedDots}
					title="Connectors"
					description="Look up live data from your own API or an MCP server."
					status={false}
				/>
			</div>
		</>
	)
}

// ---------------------------------------------------------------------------
// The connect step

const cardSurface =
	"rounded-[10px] bg-black/[0.02] p-4 ring-1 ring-black/5 dark:bg-gray-850 dark:ring-white/10"

function GithubOption({ onConnect }: { onConnect?: () => void }) {
	return (
		<div className={cardSurface}>
			<div className="flex items-start gap-3">
				<GitHubIcon className="mt-0.5 size-[18px] flex-none" />
				<div className="min-w-0 flex-1">
					<div className="flex items-center gap-2">
						<span className="text-[0.9rem] font-medium">Your codebase</span>
						<Badge variant="green">Recommended</Badge>
					</div>
					<p className="mt-1 text-[13px] text-tertiary">
						We write an article for every screen in your app and update it as
						you ship. Read-only.
					</p>
					<div className="mt-3 flex items-center gap-2">
						<Button className="h-9" Icon={GitHubIcon} onClick={onConnect}>
							Connect GitHub
						</Button>
						<Button variant="ghost" className="h-9" Icon={EnvelopeSend}>
							Ask a developer
						</Button>
					</div>
				</div>
			</div>
		</div>
	)
}

function HelpCenterOption({ name }: { name: string }) {
	return (
		<div className={cardSurface}>
			<div className="flex items-start gap-3">
				<Globe className="mt-0.5 size-[18px] flex-none text-tertiary" />
				<div className="min-w-0 flex-1">
					<div className="text-[0.9rem] font-medium">Your help center</div>
					<p className="mt-1 text-[13px] text-tertiary">
						Paste its address. Every page becomes an article.
					</p>
					<div className="mt-3 flex items-start gap-2">
						<Input
							name={name}
							className="flex-1"
							inputClassName="h-9 rounded-lg"
							placeholder="help.acme.com"
						/>
						<Button variant="outline" className="h-9 flex-none">
							Import
						</Button>
					</div>
				</div>
			</div>
		</div>
	)
}

function ChooseSource({
	name,
	onConnect,
}: {
	name: string
	onConnect?: () => void
}) {
	return (
		<div className="space-y-2.5">
			<GithubOption onConnect={onConnect} />
			<HelpCenterOption name={name} />
		</div>
	)
}

// Version C: the same two sources, plus what customers get today and a way past.
function SoftGate({
	name,
	onConnect,
	onTurnOnAnyway,
}: {
	name: string
	onConnect?: () => void
	onTurnOnAnyway?: () => void
}) {
	return (
		<div className="space-y-2.5">
			<div className={cardSurface}>
				<div className="text-xs text-tertiary">
					What a customer gets from it today
				</div>
				<p className="mt-1.5 text-[13px] text-primary">
					&quot;I could not find that in what I know. I have passed your
					question to the team.&quot;
				</p>
			</div>
			<GithubOption onConnect={onConnect} />
			<HelpCenterOption name={name} />
			<div className="flex justify-center pt-1">
				<Button
					variant="link"
					className="opacity-70 hover:opacity-80"
					onClick={onTurnOnAnyway}
				>
					Turn on anyway
				</Button>
			</div>
		</div>
	)
}

function StepRow({
	done,
	title,
	detail,
	children,
}: {
	done: boolean
	title: ReactNode
	detail: string
	children?: ReactNode
}) {
	return (
		<div className="flex items-start gap-3 p-3.5">
			{done ? (
				<span className="mt-0.5 flex size-5 flex-none items-center justify-center rounded-full bg-green-500 text-white">
					<Check className="size-3 flex-none" />
				</span>
			) : (
				<span className="mt-0.5 size-5 flex-none rounded-full ring-1 ring-gray-300 dark:ring-gray-700" />
			)}
			<div className="min-w-0 flex-1">
				<div className="text-[0.9rem] font-medium">{title}</div>
				<p className="text-[13px] text-tertiary">{detail}</p>
				{children}
			</div>
		</div>
	)
}

function ArticlesProgress() {
	return (
		<ProgressBar
			className="mt-2.5"
			percentage={(SCREENS_DONE / SCREENS_TOTAL) * 100}
		/>
	)
}

function Writing({ action }: { action: ReactNode }) {
	return (
		<div>
			<div className="divide-y divide-gray-200 rounded-[10px] ring-1 ring-black/5 dark:divide-gray-700/80 dark:ring-white/10">
				<StepRow done title="GitHub connected" detail={`Reading ${REPO}`} />
				<StepRow
					done={false}
					title="Articles"
					detail={`${SCREENS_DONE} of ${SCREENS_TOTAL} screens written`}
				>
					<ArticlesProgress />
				</StepRow>
			</div>
			<div className="mt-4 flex justify-end">{action}</div>
		</div>
	)
}

// The dialog chrome, drawn in place so every step shows on one page.
function DialogFrame({
	children,
	title,
	description,
}: {
	children: ReactNode
	title: string
	description: string
}) {
	return (
		<div className="w-full max-w-[30rem] rounded-xl bg-white p-5 shadow-card ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10">
			<div className="text-[15px] font-medium">{title}</div>
			<p className="mt-1 text-[13px] text-secondary">{description}</p>
			<div className="mt-4">{children}</div>
		</div>
	)
}

// Version B: the setup card that sits where the agent switch and coverage card go.
function SetupCard({
	id,
	writing,
	onConnect,
	onTurnOn,
}: {
	id: string
	writing: boolean
	onConnect?: () => void
	onTurnOn?: () => void
}) {
	return (
		<SettingsContainer>
			<div>
				<SettingItem
					Icon={Atom}
					title={<Titled mark="New">Set up the Productlane Agent</Titled>}
					description="Give it your code or your help center to read."
					action={
						<Badge variant={writing ? "green" : "default"}>
							{writing ? "Ready" : "Not live"}
						</Badge>
					}
				/>
				<div className="mt-4 divide-y divide-gray-200 rounded-[10px] ring-1 ring-black/5 dark:divide-gray-700/80 dark:ring-white/10">
					<StepRow
						done={writing}
						title={
							<span className="flex items-center gap-2">
								Connect your code
								{writing ? null : <Badge variant="green">Recommended</Badge>}
							</span>
						}
						detail={
							writing
								? `Reading ${REPO}. ${SCREENS_DONE} of ${SCREENS_TOTAL} screens written.`
								: "We write an article for every screen in your app."
						}
					>
						{writing ? (
							<ArticlesProgress />
						) : (
							<div className="mt-3 flex items-center gap-2">
								<Button className="h-9" Icon={GitHubIcon} onClick={onConnect}>
									Connect GitHub
								</Button>
								<Button variant="ghost" className="h-9" Icon={EnvelopeSend}>
									Ask a developer
								</Button>
							</div>
						)}
					</StepRow>
					<StepRow
						done={false}
						title="Import your help center"
						detail="Paste its address. Every page becomes an article."
					>
						<div className="mt-3 flex items-start gap-2">
							<Input
								name={`${id}-setup-help-center-url`}
								className="flex-1"
								inputClassName="h-9 rounded-lg"
								placeholder="help.acme.com"
							/>
							<Button variant="outline" className="h-9 flex-none">
								Import
							</Button>
						</div>
					</StepRow>
				</div>
				<div className="mt-4 flex items-center justify-between gap-4">
					<p className="text-xs text-secondary">
						One source is enough. Channels to deploy on follow.
					</p>
					<Button
						className="h-9 flex-none"
						Icon={Atom}
						disabled={!writing}
						onClick={onTurnOn}
					>
						Turn on agent
					</Button>
				</div>
			</div>
		</SettingsContainer>
	)
}

// ---------------------------------------------------------------------------
// Page 2: Settings / AI / Agent (ai-chat-settings-page.tsx)

// SettingsSection's markup, with a ReactNode title so a marker fits beside it.
function Section({
	title,
	description,
	className,
}: {
	title: ReactNode
	description?: ReactNode
	className?: string
}) {
	return (
		<div className={cn("mt-8 flex items-center justify-between", className)}>
			<div>
				<div className="text-sm font-medium">{title}</div>
				{description && (
					<p className="mt-1 text-xs text-secondary">{description}</p>
				)}
			</div>
		</div>
	)
}

type DeployName = "email" | "slack" | "teams" | "api" | "portal" | "requests"

const DEPLOY_CHANNELS: {
	name?: DeployName
	Icon: IconType
	title: string
	description: string
}[] = [
	{
		Icon: MessagesDuotoneIcon,
		title: "Live chat",
		description: "Answers customers in the widget chat as they type.",
	},
	{
		name: "email",
		Icon: EnvelopeDuotoneIcon,
		title: "Email",
		description:
			"Answers inbound email, and widget feedback that gets a reply by email.",
	},
	{
		name: "slack",
		Icon: SlackIconGray,
		title: "Slack",
		description: "Answers in the Slack Connect thread the customer wrote in.",
	},
	{
		name: "teams",
		Icon: MicrosoftTeamsIcon,
		title: "Microsoft Teams",
		description: "Answers in the Teams channel the customer wrote in.",
	},
	{
		name: "api",
		Icon: ApiCode,
		title: "API",
		description: "Answers threads created through the public API.",
	},
	{
		name: "portal",
		Icon: LifeRing,
		title: "Support portal",
		description: "Answers messages customers send from the support portal.",
	},
	{
		name: "requests",
		Icon: FeatureRequests,
		title: "Feature requests",
		description:
			"Answers feature requests submitted in the portal. Upvotes stay untouched.",
	},
]

const num = (value: number) => value.toLocaleString("en-US")
const compact = (value: number) =>
	value >= 1000 ? `${Math.round(value / 1000)}k` : `${value}`

// KnowledgeCoverageCard at 0 articles: the score gives way to a Connect GitHub action.
function EmptyCoverageCard({ onConnect }: { onConnect: () => void }) {
	return (
		<SettingsContainer>
			<div>
				<SettingItem
					Icon={BookOpen}
					title={<Titled mark="Changed">Knowledge coverage</Titled>}
					description="How much the agent has to answer from"
					action={<Badge variant="default">Empty</Badge>}
				/>
				<div className="mt-4 flex items-start gap-3 border-t border-gray-200 pt-4 dark:border-gray-700/80">
					<GitHubIcon className="mt-0.5 size-[18px] flex-none" />
					<div className="min-w-0 flex-1">
						<div className="text-sm font-medium">Start with your code</div>
						<p className="mt-0.5 text-xs text-secondary">
							We write an article for every screen in your app.
						</p>
					</div>
					<div className="flex flex-none items-center gap-2">
						<Button variant="outline" size="sm" onClick={onConnect}>
							Import docs
						</Button>
						<Button size="sm" Icon={GitHubIcon} onClick={onConnect}>
							Connect GitHub
						</Button>
					</div>
				</div>
			</div>
		</SettingsContainer>
	)
}

// KnowledgeCoverageCard exactly as production, fed mock numbers.
function CoverageCard() {
	const { chars, articles } = CONNECTED_COVERAGE
	const score = coverageScore(chars)
	const bandIndex = coverageTierIndex(chars) - 1
	const advice = coverageAdvice(CONNECTED_COVERAGE)

	return (
		<SettingsContainer>
			<div>
				<SettingItem
					Icon={BookOpen}
					title="Knowledge coverage"
					description="How much the agent has to answer from"
					action={
						<Badge variant={score >= 50 ? "green" : "default"}>
							{coverageTierLabel(chars)}
						</Badge>
					}
				/>
				<div className="mt-4 flex items-end justify-between gap-4">
					<div className="flex items-baseline gap-1">
						<span className="text-[32px] font-medium tabular-nums leading-none text-primary">
							{score}
						</span>
						<span className="text-sm text-tertiary">/100</span>
					</div>
					<span className="text-[11px] tabular-nums text-tertiary">
						{num(chars)} characters in {num(articles)} articles
					</span>
				</div>
				<div className="relative mt-3 h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
					<div
						className="h-full rounded-full bg-green-500 dark:bg-green-400"
						style={{ width: `${score}%` }}
					/>
					{[25, 50, 75].map((left) => (
						<span
							key={left}
							className="absolute top-0 h-full w-px bg-white dark:bg-gray-850"
							style={{ left: `${left}%` }}
						/>
					))}
				</div>
				<div className="mt-1.5 flex text-[10px] text-tertiary">
					{COVERAGE_BANDS.map((band, index) => (
						<span
							key={band.label}
							className={cn(
								"w-1/4 text-center",
								index === bandIndex && "text-primary",
							)}
						>
							{band.label}{" "}
							<span className="opacity-70">{compact(band.to)}</span>
						</span>
					))}
				</div>
				<p className="mt-4 border-t border-gray-200 pt-3 text-xs leading-relaxed text-secondary dark:border-gray-700/80">
					{advice.tip}{" "}
					<span className="text-primary underline">{advice.action}</span>
				</p>
			</div>
		</SettingsContainer>
	)
}

// A locked switch keeps responding: the click opens the connect step.
function LockedSwitch({
	name,
	checked,
	onBlockedClick,
}: {
	name: string
	checked: boolean
	onBlockedClick: () => void
}) {
	return (
		// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
		<div onClick={onBlockedClick}>
			<Switch
				name={name}
				label={null}
				checked={checked}
				disabled
				switchClassName="pointer-events-none"
			/>
		</div>
	)
}

function AgentSettingsReplica({
	id,
	version,
	initiallyConnected,
}: {
	id: string
	version: Version
	initiallyConnected: boolean
}) {
	const [connected, setConnected] = useState(initiallyConnected)
	const [enabled, setEnabled] = useState(false)
	const [deploy, setDeploy] = useState<Record<DeployName, boolean>>({
		email: true,
		slack: false,
		teams: false,
		api: false,
		portal: true,
		requests: false,
	})
	const [linearIssues, setLinearIssues] = useState(true)
	const [gateOpen, setGateOpen] = useState(false)
	const [step, setStep] = useState<"choose" | "writing">("choose")
	const [setupWriting, setSetupWriting] = useState(false)

	const empty = !connected
	// A locks every switch; B hides them; C keeps them working.
	const locked = version === "A" && empty
	const setupMode = version === "B" && empty
	const softMode = version === "C" && empty

	const openGate = () => {
		setStep("choose")
		setGateOpen(true)
	}
	const dimmedClass = cn(
		"transition-opacity duration-200 ease-in-out",
		!enabled && !setupMode && "opacity-50",
	)

	const toggleAgent = (value: boolean) => {
		if (value && softMode) {
			openGate()
			return
		}
		setEnabled(value)
	}

	const dialogTitle =
		step === "writing"
			? WRITING_TITLE
			: version === "C"
				? SOFT_TITLE
				: GATE_TITLE
	const dialogDescription =
		step === "writing"
			? version === "C"
				? WRITING_ON_DESCRIPTION
				: WRITING_DESCRIPTION
			: version === "C"
				? SOFT_DESCRIPTION
				: GATE_DESCRIPTION

	return (
		<>
			<SettingsHeader
				title="Productlane Agent"
				subtitle="Resolve customer questions with answers grounded in your docs."
				docsArticle="c8cfc4d9-dec0-4188-b31c-f55ffd758d58"
			/>

			<div className="flex min-w-0 flex-col gap-4">
				{setupMode ? (
					<SetupCard
						id={id}
						writing={setupWriting}
						onConnect={() => setSetupWriting(true)}
						onTurnOn={() => {
							setConnected(true)
							setEnabled(true)
						}}
					/>
				) : (
					<>
						<SettingsContainer>
							<SettingItem
								Icon={Atom}
								title={
									<Titled mark={empty ? "Changed" : undefined}>
										Productlane Agent
									</Titled>
								}
								description={
									empty ? AGENT_SWITCH_DESCRIPTION[version] : AGENT_DESCRIPTION
								}
								action={
									<div className="flex items-center gap-2">
										{locked ? (
											<LockedSwitch
												name={`${id}-isEnabled`}
												checked={false}
												onBlockedClick={openGate}
											/>
										) : (
											<Switch
												name={`${id}-isEnabled`}
												label={null}
												checked={enabled}
												onCheckedChange={toggleAgent}
											/>
										)}
									</div>
								}
							/>
						</SettingsContainer>

						{empty ? (
							<EmptyCoverageCard onConnect={openGate} />
						) : (
							<CoverageCard />
						)}
					</>
				)}

				{setupMode ? null : (
					<>
						<Section
							className={dimmedClass}
							title={
								<Titled mark={locked ? "Changed" : undefined}>Deploy</Titled>
							}
							description={
								locked ? DEPLOY_GATED_DESCRIPTION : DEPLOY_DESCRIPTION
							}
						/>
						<SettingsContainer className={cn("mt-1", dimmedClass)}>
							{DEPLOY_CHANNELS.map((channel, index) => {
								const { name } = channel
								return (
									<SettingItem
										key={channel.title}
										Icon={channel.Icon}
										title={channel.title}
										description={channel.description}
										className={index > 0 ? "pt-3" : undefined}
										action={
											!name ? (
												<div>
													<Switch
														name={`${id}-liveChat`}
														checked
														disabled
														switchClassName="pointer-events-none"
													/>
												</div>
											) : locked ? (
												<LockedSwitch
													name={`${id}-${name}`}
													checked={false}
													onBlockedClick={openGate}
												/>
											) : (
												<Switch
													name={`${id}-${name}`}
													checked={deploy[name]}
													onCheckedChange={(value) =>
														setDeploy((prev) => ({ ...prev, [name]: value }))
													}
												/>
											)
										}
									/>
								)
							})}
						</SettingsContainer>

						<SettingsContainer className={dimmedClass}>
							<SettingItem
								Icon={GitBranchPlus}
								title="Create Linear issues from agent chats"
								description={
									<>
										The agent files bugs and feature requests to Linear. Group
										reports in{" "}
										<span className="text-primary underline">Routines</span>.
									</>
								}
								action={
									<Switch
										name={`${id}-createLinearIssues`}
										checked={linearIssues}
										onCheckedChange={setLinearIssues}
									/>
								}
							/>
						</SettingsContainer>

						<SettingsContainer className={dimmedClass}>
							<SettingItem
								Icon={Bug}
								title="Auto-assign Productlane Agent to bugs"
								description={
									<>
										Requires a connected coding agent.{" "}
										<span className="text-primary underline">
											Connect Cursor
										</span>{" "}
										to enable.
									</>
								}
								action={
									<Switch
										name={`${id}-autoAssignAgentToBugs`}
										checked={false}
										disabled
									/>
								}
							/>
						</SettingsContainer>
					</>
				)}

				<Section
					className={dimmedClass}
					title="Customize"
					description="Customize the AI chat messages and suggestions."
				/>
				<SettingsContainer
					className={cn(
						"mt-1 flex flex-col gap-4 divide-none p-4",
						dimmedClass,
					)}
				>
					<Textarea
						variant="settings"
						name={`${id}-welcomeMessage`}
						label="Welcome Message"
						placeholder="Welcome Message"
						defaultValue={defaultAiChatWelcomeMessage(WORKSPACE_NAME)}
					/>
					<div className="flex flex-col gap-2">
						<Label className="mb-0.5 block">Suggestions</Label>
						{docsDefaultExampleQuestions.map((question, index) => (
							<div key={index}>
								<Input
									name={`${id}-exampleQuestions-${index}`}
									defaultValue={question}
									placeholder={`Suggestion ${index + 1}`}
								/>
							</div>
						))}
					</div>
				</SettingsContainer>

				<Section
					className={dimmedClass}
					title="Workspace guidance"
					description="Save instructions and writing preferences for the Productlane Agent across all channels. They apply until you change them."
				/>
				<div className={dimmedClass}>
					<CollapsiblePromptTextarea
						variant="settings"
						name={`${id}-customInstructions`}
						placeholder="Optional agent guidance..."
						minRows={4}
					/>
					<div className="mt-3 flex flex-col gap-2">
						<div className="flex justify-end">
							<Button variant="outline" size="sm" Icon={Atom}>
								Learn writing style from replies
							</Button>
						</div>
					</div>
				</div>
			</div>

			<Dialog
				isOpen={gateOpen}
				onOpenChange={setGateOpen}
				title={dialogTitle}
				description={dialogDescription}
			>
				{step === "choose" ? (
					version === "C" ? (
						<SoftGate
							name={`${id}-gate-help-center-url`}
							onConnect={() => {
								setEnabled(true)
								setStep("writing")
							}}
							onTurnOnAnyway={() => {
								setEnabled(true)
								setGateOpen(false)
							}}
						/>
					) : (
						<ChooseSource
							name={`${id}-gate-help-center-url`}
							onConnect={() => setStep("writing")}
						/>
					)
				) : (
					<Writing
						action={
							version === "C" ? (
								<Button
									className="h-9"
									onClick={() => {
										setConnected(true)
										setGateOpen(false)
									}}
								>
									Done
								</Button>
							) : (
								<Button
									className="h-9"
									Icon={Atom}
									onClick={() => {
										setConnected(true)
										setEnabled(true)
										setGateOpen(false)
									}}
								>
									Turn on agent
								</Button>
							)
						}
					/>
				)}
			</Dialog>
		</>
	)
}

// ---------------------------------------------------------------------------

const VERSIONS: Version[] = ["A", "B", "C"]

export function AgentKnowledgeGatePage() {
	if (!isDevEnvironment) return <NotFound />

	return (
		<div className="h-dvh overflow-y-auto bg-gray-100 pb-24 dark:bg-gray-950">
			<div className="px-10 py-12">
				<div className="max-w-3xl">
					<DesignIssueHeader
						identifier="PRO-10725"
						title="Require a knowledge source (GitHub or docs) before the AI agent can go live"
						url="https://linear.app/productlane/issue/PRO-10725"
						summary="A team turned the agent on with nothing for it to read, so it never answered anyone, and they turned it off. With this design, the agent asks for GitHub or a help center first. Connecting GitHub writes an article for every screen in the app."
					/>
					<p className="mt-4 text-[13px] text-tertiary">
						Three versions side by side; read a column top to bottom as one
						version. Every page is a copy of production with mock data, and only
						parts marked <Mark kind="New" /> or <Mark kind="Changed" /> differ.
						Click the switches and buttons to try each flow. The onboarding
						slide for GitHub is at{" "}
						<a
							href="/design/onboarding-github"
							className="text-primary underline"
						>
							/design/onboarding-github
						</a>
						.
					</p>
				</div>

				<div className="mt-12 space-y-16">
					<SettingsCardInsetContext.Provider value={true}>
						<ScreenRow
							title="Settings / AI"
							note="The AI overview with no knowledge source. Only the Agent row changes."
						>
							{VERSIONS.map((version) => (
								<Column
									key={version}
									version={version}
									line={VERSION_LINE[version]}
								>
									<SettingsFrame>
										<AiOverviewReplica version={version} />
									</SettingsFrame>
								</Column>
							))}
						</ScreenRow>
					</SettingsCardInsetContext.Provider>

					<SettingsCardInsetContext.Provider value={false}>
						<ScreenRow
							title="Settings / AI / Agent (no knowledge source)"
							note="0 articles and no repo. Each version is live: click the agent switch, a Deploy switch, or Connect GitHub."
						>
							{VERSIONS.map((version) => (
								<Column
									key={version}
									version={version}
									line={VERSION_LINE[version]}
								>
									<SettingsFrame>
										<AgentSettingsReplica
											id={`empty-${version}`}
											version={version}
											initiallyConnected={false}
										/>
									</SettingsFrame>
								</Column>
							))}
						</ScreenRow>
					</SettingsCardInsetContext.Provider>

					<ScreenRow
						title="Settings / AI / Agent / Connect a knowledge source"
						note="The step that opens when someone tries to turn the agent on. GitHub comes first because it works for a team with no docs."
					>
						<Column
							version="A"
							line="Opens from any locked switch. No way past it."
						>
							<DialogFrame title={GATE_TITLE} description={GATE_DESCRIPTION}>
								<ChooseSource name="frame-a-help-center-url" />
							</DialogFrame>
						</Column>
						<Column
							version="B"
							line="No dialog. Connect GitHub fills the setup card in place."
						>
							<SettingsCardInsetContext.Provider value={false}>
								<SetupCard id="frame-b" writing />
							</SettingsCardInsetContext.Provider>
						</Column>
						<Column
							version="C"
							line="Opens when the agent switch goes on. Shows what customers get today."
						>
							<DialogFrame title={SOFT_TITLE} description={SOFT_DESCRIPTION}>
								<SoftGate name="frame-c-help-center-url" />
							</DialogFrame>
						</Column>
					</ScreenRow>

					<ScreenRow
						title="Settings / AI / Agent / After GitHub connects"
						note={`The backfill starts at once and the agent can go live after the first article, so nobody waits for all ${SCREENS_TOTAL} screens.`}
					>
						<Column
							version="A"
							line="Turn on agent is the last step of the dialog."
						>
							<DialogFrame
								title={WRITING_TITLE}
								description={WRITING_DESCRIPTION}
							>
								<Writing
									action={
										<Button className="h-9" Icon={Atom}>
											Turn on agent
										</Button>
									}
								/>
							</DialogFrame>
						</Column>
						<Column
							version="B"
							line="Same card as above; Turn on agent unlocks and Deploy appears after."
						>
							<p className="text-[13px] text-tertiary">
								Shown in the row above.
							</p>
						</Column>
						<Column
							version="C"
							line="The agent is already on; the dialog only confirms."
						>
							<DialogFrame
								title={WRITING_TITLE}
								description={WRITING_ON_DESCRIPTION}
							>
								<Writing action={<Button className="h-9">Done</Button>} />
							</DialogFrame>
						</Column>
					</ScreenRow>

					<SettingsCardInsetContext.Provider value={false}>
						<section>
							<div className="mb-4">
								<div className="text-xs font-medium text-tertiary">
									Settings / AI / Agent (source connected, all versions)
								</div>
								<p className="mt-1 max-w-[704px] text-[13px] text-secondary">
									After a source connects, the page is production again: the
									switches work and the coverage card shows the score as the
									articles come in.
								</p>
							</div>
							<SettingsFrame>
								<AgentSettingsReplica
									id="connected"
									version="A"
									initiallyConnected
								/>
							</SettingsFrame>
						</section>
					</SettingsCardInsetContext.Provider>
				</div>
			</div>
		</div>
	)
}
